import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Checkbox,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";
import TextField from "../../../components/carbon/Form/TextField";
import TextareaField from "../../../components/carbon/Form/TextareaField";
import {
  useCreateRole,
  useRoleAbilities,
  useUpdateRole,
} from "../../../hooks/useRoles";
import { useNotification } from "../../../context";
import { Role } from "../../../types";
import { roleValidationSchema, RoleFormValues } from "./roleValidationSchema";
import "./roleForm.scss";

type RoleFormProps = {
  role: Role | null;
  open: boolean;
  setOpen: (open: boolean) => void;
};

/**
 * The create/edit role dialog — `UserGroupForm`'s shape (Carbon `ComposedModal`, RHF + Yup for the
 * text fields) with the member list replaced by an ability-checkbox grid driven by the backend
 * catalog (`GET /roles/abilities`), grouped by `AbilityCatalogEntry.group`.
 *
 * Abilities are a plain `Set<string>` state, not RHF — a role may have none, so there is nothing
 * to validate.
 *
 * Every role is fully editable, built-ins included: name, description and abilities. The protected
 * Administrator is the exception for abilities: it always holds every ability of its side (the
 * backend resolves and saves it that way), so its checkboxes show the whole catalog, ticked and
 * locked — only its name and description are editable. The backend is the authority — the one edit
 * it refuses is the one that would leave nobody able to manage roles, and that error is surfaced via
 * `handleSaveError`.
 */
const RoleForm = ({ role, open, setOpen }: RoleFormProps) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const { data: catalog = [] } = useRoleAbilities(open);

  const [selectedAbilities, setSelectedAbilities] = useState<Set<string>>(
    new Set()
  );

  const close = () => setOpen(false);
  const isAdministrator = !!role?.protectedRole;

  // Prefer the server's own message (guard errors, duplicate names) over a generic fallback.
  const handleSaveError = (error: any) =>
    openNotification(error?.response?.data?.message || t("roles.saveError"));

  const createRole = useCreateRole(close, handleSaveError);
  const updateRole = useUpdateRole(close, handleSaveError);
  const isSaving = createRole.isPending || updateRole.isPending;

  const defaultValues: RoleFormValues = useMemo(
    () => ({ name: role?.name ?? "", description: role?.description ?? "" }),
    [role]
  );

  const { control, handleSubmit, reset } = useForm<RoleFormValues>({
    resolver: yupResolver(roleValidationSchema),
    defaultValues,
  });

  // Re-seed whenever the dialog opens for a different role (or fresh for a new one).
  useEffect(() => {
    reset(defaultValues);
    setSelectedAbilities(new Set(role?.abilities ?? []));
  }, [role, open, reset, defaultValues]);

  // Group by `group`, preserving the catalog's own order for both groups and abilities.
  const groups = useMemo(() => {
    const byGroup = new Map<string, typeof catalog>();
    catalog.forEach((entry) => {
      const list = byGroup.get(entry.group) ?? [];
      list.push(entry);
      byGroup.set(entry.group, list);
    });
    return Array.from(byGroup.entries());
  }, [catalog]);

  const toggle = (value: string) =>
    setSelectedAbilities((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  const shownAbilities = isAdministrator
    ? new Set(catalog.map((entry) => entry.value))
    : selectedAbilities;

  const onValid = (values: RoleFormValues) => {
    const payload = {
      name: values.name,
      description: values.description ?? "",
      abilities: Array.from(shownAbilities),
    };
    if (role) {
      updateRole.mutate({ id: role.id, ...payload });
    } else {
      createRole.mutate(payload);
    }
  };

  const title = role ? t("roles.edit") : t("roles.add");

  return (
    <ComposedModal open={open} size="md" aria-label={title} onClose={close}>
      <ModalHeader title={title} />
      <ModalBody hasForm>
        <TextField
          name="name"
          label={t("roles.name")}
          placeholder={t("roles.namePlaceholder")}
          control={control}
          required
        />

        <TextareaField
          name="description"
          label={t("roles.description")}
          placeholder={t("roles.descriptionPlaceholder")}
          control={control}
          optional
        />

        <div className="role-form__abilities">
          <p className="role-form__abilities-label">{t("roles.abilities")}</p>
          {isAdministrator ? (
            <p className="role-form__note">
              {t("roles.administratorAllAbilities")}
            </p>
          ) : null}
          <div className="role-form__ability-list">
            {groups.map(([group, entries]) => (
              <fieldset
                key={group}
                className="role-form__group"
                aria-labelledby={`role-group-${group}`}
              >
                <h4
                  id={`role-group-${group}`}
                  className="role-form__group-heading"
                >
                  {t(`roles.groups.${group}`, {
                    defaultValue:
                      group.charAt(0).toUpperCase() + group.slice(1),
                  })}
                </h4>
                {entries.map((entry) => (
                  <Checkbox
                    key={entry.value}
                    id={`role-ability-${entry.value}`}
                    labelText={t(entry.labelKey)}
                    checked={shownAbilities.has(entry.value)}
                    disabled={isAdministrator}
                    onChange={() => toggle(entry.value)}
                  />
                ))}
              </fieldset>
            ))}
          </div>
        </div>
      </ModalBody>
      <ModalFooter
        secondaryButtonText={t("cta.cancel")}
        primaryButtonText={isSaving ? t("cta.saving") : t("cta.save")}
        primaryButtonDisabled={isSaving}
        loadingStatus={isSaving ? "active" : "inactive"}
        loadingDescription={t("cta.loading")}
        onRequestSubmit={handleSubmit(onValid)}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default RoleForm;
